"use client";

import type React from "react";

import { useState } from "react";
import { motion } from "motion/react";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle,
  FileText,
  Search,
  Bot,
  Clock,
} from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LoginRequired from "@/components/auth/login-required";

const HomeworkHelperForm = z.object({
  subject: z.string().min(1, "Please select a subject"),
  problem: z.string().optional(),
  class: z.string().optional(),
  chapter: z.string().optional(),
});

export default function HomeworkHelperPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [solution, setSolution] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<z.infer<typeof HomeworkHelperForm>>({
    resolver: zodResolver(HomeworkHelperForm as any),
    defaultValues: {
      subject: "",
      problem: "",
      class: "",
      chapter: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof HomeworkHelperForm>) => {
    const { subject, problem, class: className, chapter } = data;
    setIsSubmitted(true);
    setIsLoading(true);
    setProgress(0);
    setSolution(null);

    try {
      const response = await fetch("/api/ai/homework", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ subject, problem, class: className, chapter }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to get homework help");
      }

      setSolution(data.response);
      toast.success("Homework solution ready!");
    } catch (error: unknown) {
      console.error("Homework Helper Error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setSolution(
        `Sorry, I encountered an error: ${errorMessage}. Please try again.`
      );

      if (errorMessage.includes("Authentication required")) {
        toast.error("Please log in to use homework helper");
      } else {
        toast.error("Failed to get homework help");
      }
    } finally {
      setIsLoading(false);
      setProgress(100);
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    reset();
    setSolution(null);
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <LoginRequired
      message="Sign in to access our AI Homework Helper and get personalized learning support."
      feature="homework"
    >
      <div className="container mx-auto py-10 px-4 md:px-6">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <Link
            href="/ai"
            className="inline-flex items-center text-primary hover:text-primary/80 mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to AI Learning Hub
          </Link>

          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Homework Helper
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Get step-by-step guidance for your assignments and homework
            challenges
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <Card className="h-full">
              {/* <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-t-lg">
              <div className="flex items-center">
                <Sparkles className="h-6 w-6 mr-2" />
                <CardTitle>Homework Assistant</CardTitle>
              </div>
              <CardDescription className="text-orange-100">
                Submit your homework questions to get detailed guidance
              </CardDescription>
            </CardHeader> */}
              <CardContent className="pt-6">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Subject
                      </label>
                      <Select
                        onValueChange={(value) => {
                          setValue("subject", value);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="math">Mathematics</SelectItem>
                          <SelectItem value="science">Science</SelectItem>
                          <SelectItem value="english">English</SelectItem>
                          <SelectItem value="social">Social Studies</SelectItem>
                          <SelectItem value="nepali">Nepali</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="text-destructive">
                        {errors.subject?.message}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Your Question
                      </label>
                      <Textarea
                        {...register("problem")}
                        placeholder="Type your homework question or problem here..."
                        className="min-h-[150px]"
                      />
                      <div className="text-destructive">
                        {errors.problem?.message}
                      </div>
                    </div>

                    {/* two more inputs */}
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Class
                      </label>
                      <Input
                        {...register("class")}
                        placeholder="Enter your class (optional)"
                      />
                      <div className="text-destructive">
                        {errors.class?.message}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Chapter
                      </label>
                      <Input
                        {...register("chapter")}
                        placeholder="Enter the chapter (optional)"
                      />
                      <div className="text-destructive">
                        {errors.chapter?.message}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={!isValid}
                    >
                      Get Homework Help
                    </Button>
                  </form>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-accent/30 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Your Query</h3>
                      <p className="text-gray-700 dark:text-gray-300">
                        Problem:
                        <span className="font-semibold ml-2">
                          {watch("problem")}
                        </span>
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        Class:
                        <span className="font-semibold ml-2">
                          {watch("class")}
                        </span>
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        Chapter:
                        <span className="font-semibold ml-2">
                          {watch("chapter")}
                        </span>
                      </p>
                      <div className="flex items-center mt-2">
                        <Badge variant="outline" className="mr-2">
                          {watch("subject")}
                        </Badge>
                        <Button variant="ghost" size="sm" onClick={resetForm}>
                          Ask another question
                        </Button>
                      </div>
                    </div>

                    {isLoading ? (
                      <div className="space-y-4 py-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">
                            Analyzing your question...
                          </span>
                          <span className="text-sm font-medium">
                            {progress}%
                          </span>
                        </div>
                        <Progress value={progress} className="h-2" />
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mt-2">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>This usually takes a few seconds</span>
                        </div>
                      </div>
                    ) : solution ? (
                      <div className="bg-card border rounded-lg p-4 shadow-sm">
                        <div className="flex items-center mb-4">
                          <CheckCircle className="h-5 w-5 text-primary mr-2" />
                          <h3 className="font-semibold">Solution Ready</h3>
                        </div>
                        <div className="prose prose-sm dark:prose-invert max-w-none bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-700 rounded-lg p-6 border border-blue-200 dark:border-slate-600">
                          <Markdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              h1: ({ children }) => (
                                <h1 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-3">
                                  {children}
                                </h1>
                              ),
                              h2: ({ children }) => (
                                <h2 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
                                  {children}
                                </h2>
                              ),
                              h3: ({ children }) => (
                                <h3 className="text-md font-medium text-blue-700 dark:text-blue-300 mb-2">
                                  {children}
                                </h3>
                              ),
                              p: ({ children }) => (
                                <p className="text-slate-700 dark:text-slate-200 mb-3 leading-relaxed">
                                  {children}
                                </p>
                              ),
                              ul: ({ children }) => (
                                <ul className="text-slate-700 dark:text-slate-200 ml-4 mb-3 space-y-1">
                                  {children}
                                </ul>
                              ),
                              ol: ({ children }) => (
                                <ol className="text-slate-700 dark:text-slate-200 ml-4 mb-3 space-y-1">
                                  {children}
                                </ol>
                              ),
                              li: ({ children }) => (
                                <li className="marker:text-blue-600 dark:marker:text-blue-400">
                                  {children}
                                </li>
                              ),
                              code: ({ children }) => (
                                <code className="bg-blue-100 dark:bg-slate-600 text-blue-800 dark:text-blue-200 px-2 py-1 rounded font-mono text-sm">
                                  {children}
                                </code>
                              ),
                              blockquote: ({ children }) => (
                                <blockquote className="border-l-4 border-blue-400 pl-4 italic text-slate-600 dark:text-slate-300 my-3">
                                  {children}
                                </blockquote>
                              ),
                            }}
                          >
                            {solution}
                          </Markdown>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium mb-2">
                            Was this helpful?
                          </h4>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                resetForm();
                                toast.success("Thank you for your feedback!");
                              }}
                            >
                              Yes, thank you!
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                resetForm();
                                toast.error("Let's try again!");
                              }}
                            >
                              I need more help
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="space-y-6"
          >
            <Card>
              <CardHeader>
                <CardTitle>How It Works</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-accent text-primary flex items-center justify-center mr-3">
                      1
                    </div>
                    <div>
                      <h4 className="font-medium">Select Subject</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Choose the subject area of your homework
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-accent text-primary flex items-center justify-center mr-3">
                      2
                    </div>
                    <div>
                      <h4 className="font-medium">Enter Question</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Type your homework problem in detail
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-accent text-primary flex items-center justify-center mr-3">
                      3
                    </div>
                    <div>
                      <h4 className="font-medium">Get Solution</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Receive step-by-step guidance to solve it
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-accent text-primary flex items-center justify-center mr-3">
                      4
                    </div>
                    <div>
                      <h4 className="font-medium">Learn & Apply</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Understand the solution and apply to similar problems
                      </p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tips for Best Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start">
                  <BookOpen className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <p className="text-sm">
                    Be specific about what you&apos;re struggling with
                  </p>
                </div>
                <div className="flex items-start">
                  <FileText className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <p className="text-sm">
                    Include all relevant information from the problem
                  </p>
                </div>
                <div className="flex items-start">
                  <Search className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <p className="text-sm">
                    Show any work you&apos;ve already attempted
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <p className="text-sm">
                    Use the solution to understand concepts, not just get
                    answers
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Popular Subjects</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="math">
                  <TabsList className="grid grid-cols-3 mb-4">
                    <TabsTrigger value="math">Math</TabsTrigger>
                    <TabsTrigger value="science">Science</TabsTrigger>
                    <TabsTrigger value="english">English</TabsTrigger>
                  </TabsList>
                  <TabsContent value="math" className="space-y-2">
                    <Badge className="mr-2 mb-2">Algebra</Badge>
                    <Badge className="mr-2 mb-2">Geometry</Badge>
                    <Badge className="mr-2 mb-2">Calculus</Badge>
                    <Badge className="mr-2 mb-2">Statistics</Badge>
                    <Badge className="mr-2 mb-2">Trigonometry</Badge>
                  </TabsContent>
                  <TabsContent value="science" className="space-y-2">
                    <Badge className="mr-2 mb-2">Biology</Badge>
                    <Badge className="mr-2 mb-2">Chemistry</Badge>
                    <Badge className="mr-2 mb-2">Physics</Badge>
                    <Badge className="mr-2 mb-2">Earth Science</Badge>
                    <Badge className="mr-2 mb-2">Astronomy</Badge>
                  </TabsContent>
                  <TabsContent value="english" className="space-y-2">
                    <Badge className="mr-2 mb-2">Grammar</Badge>
                    <Badge className="mr-2 mb-2">Literature</Badge>
                    <Badge className="mr-2 mb-2">Essay Writing</Badge>
                    <Badge className="mr-2 mb-2">Poetry</Badge>
                    <Badge className="mr-2 mb-2">Comprehension</Badge>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <h2 className="text-2xl font-bold mb-4">
            Explore Other AI Learning Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <Link href="/ai/chat">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
                      <Bot className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    AI Chat Assistant
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Get personalized learning support through our intelligent
                    chat interface
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link href="/ai/research-assistant">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                      <Search className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    Research Assistant
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Find reliable information and resources for your projects
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </motion.div>
      </div>
    </LoginRequired>
  );
}
